import { BufferGeometry, Float32BufferAttribute } from 'three'

// Voxel models. The squirrel and acorn are built from the same pixel sprites as
// the flat view, so seen side-on their silhouettes match the sprites exactly.
// '#' = body voxels, 'o' = accent voxels (shown on the outer faces only).

export const VOXEL = 2 // World units per voxel; one world unit is one CSS px unzoomed

export const SQUIRREL_BODY = [
  '..####..........',
  '.######.....#...',
  '####.###...##...',
  '###..###..####..',
  '##...###.##o###.',
  '#...####.#######',
  '....###########.',
  '...###########..',
]
export const SQUIRREL_LEGS = [
  ['....##....##....', '...##......##...'],
  ['.....##..##.....', '......####......'],
]
export const ACORN = ['..#..', '#####', '#o#o#', '.###.', '.#o#.', '..#..']

const range = (from, to) => {
  const out = []
  for (let i = from; i <= to; i++) out.push(i)
  return out
}

// Outward faces of a unit cube, wound counter-clockwise from outside
const FACES = [
  { n: [1, 0, 0], v: [[1, 0, 1], [1, 0, 0], [1, 1, 0], [1, 1, 1]] },
  { n: [-1, 0, 0], v: [[0, 0, 0], [0, 0, 1], [0, 1, 1], [0, 1, 0]] },
  { n: [0, 1, 0], v: [[0, 1, 1], [1, 1, 1], [1, 1, 0], [0, 1, 0]] },
  { n: [0, -1, 0], v: [[0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1]] },
  { n: [0, 0, 1], v: [[0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1]] },
  { n: [0, 0, -1], v: [[1, 0, 0], [0, 0, 0], [0, 1, 0], [1, 1, 0]] },
]

const key = (x, y, z) => `${x},${y},${z}`

// Turns sprite rows into voxels. depthOf(col, row) gives the z indices a pixel
// is extruded through; matOf(col, row, char, isOuter) picks its material.
const spriteVoxels = (rows, depthOf, matOf, yOffset = 0) => {
  const voxels = new Map()
  rows.forEach((row, r) => {
    const y = rows.length - 1 - r + yOffset
    for (let c = 0; c < row.length; c++) {
      const ch = row[c]
      if (ch === '.') continue
      const zs = depthOf(c, r)
      const zMin = Math.min(...zs)
      const zMax = Math.max(...zs)
      zs.forEach((z) => voxels.set(key(c, y, z), matOf(c, r, ch, z === zMin || z === zMax)))
    }
  })
  return voxels
}

// Meshes voxels into one geometry with only exposed faces, with a draw group
// per material index. offset is in voxels, before scaling.
const meshVoxels = (voxels, offset, scale = VOXEL) => {
  const buckets = []
  voxels.forEach((mat, k) => {
    const [x, y, z] = k.split(',').map(Number)
    if (!buckets[mat]) buckets[mat] = { pos: [], norm: [] }
    FACES.forEach(({ n, v }) => {
      if (voxels.has(key(x + n[0], y + n[1], z + n[2]))) return
      const quad = v.map(([dx, dy, dz]) => [
        (x + dx + offset[0]) * scale,
        (y + dy + offset[1]) * scale,
        (z + dz + offset[2]) * scale,
      ])
      ;[0, 1, 2, 0, 2, 3].forEach((i) => {
        buckets[mat].pos.push(...quad[i])
        buckets[mat].norm.push(...n)
      })
    })
  })
  const pos = []
  const norm = []
  const geometry = new BufferGeometry()
  buckets.forEach((b, mat) => {
    if (!b) return
    geometry.addGroup(pos.length / 3, b.pos.length / 3, mat)
    pos.push(...b.pos)
    norm.push(...b.norm)
  })
  geometry.setAttribute('position', new Float32BufferAttribute(pos, 3))
  geometry.setAttribute('normal', new Float32BufferAttribute(norm, 3))
  return geometry
}

// ----- Squirrel: materials 0 fur, 1 eye, 2 tail -----
const squirrelDepth = (c, r) => {
  if (r <= 2 && c >= 11) return [-2, 1] // ears
  if (r <= 5) return range(-2, 1) // tail and head
  return range(-3, 2) // body
}
const squirrelMat = (c, r, ch, outer) => {
  if (ch === 'o' && outer) return 1
  return c <= 7 && r <= 5 ? 2 : 0
}
// Origin at the feet, centred in x and z
const SQUIRREL_OFFSET = [-SQUIRREL_BODY[0].length / 2, 0, 0]

export const buildSquirrelGeometries = () => ({
  body: meshVoxels(spriteVoxels(SQUIRREL_BODY, squirrelDepth, squirrelMat, 2), SQUIRREL_OFFSET),
  legs: SQUIRREL_LEGS.map((rows) =>
    meshVoxels(spriteVoxels(rows, () => [-3, 2], () => 0), SQUIRREL_OFFSET),
  ),
})

// ----- Acorn: materials 0 nut, 1 spots, 2 cap -----
const acornDepth = (c, r) => {
  const half = (ACORN[r].replace(/\./g, '').length - 1) / 2
  return range(-half, half)
}
const acornMat = (c, r, ch, outer) => {
  if (ch === 'o' && outer) return 1
  return r <= 2 ? 2 : 0
}
// Centred on its middle so it can roll about its centre
export const buildAcornGeometry = () =>
  meshVoxels(spriteVoxels(ACORN, acornDepth, acornMat), [
    -ACORN[0].length / 2,
    -ACORN.length / 2,
    -0.5,
  ])

// ----- Trees: materials 0 bark, 1 leaves, 2 light leaves, 3 birch bark, 4 birch marks,
// 5 vines, 6 palm bark, 7 dead grey bark, 8 cactus fruit, 9 cactus flowers,
// 10 stone, 11 a second stone (moss, lichen or banding) -----
export const TREE_VOXEL = 5
export const TREE_KINDS = ['oak', 'pine', 'birch', 'wide', 'shrub']
const leafMat = (random) => (random() < 0.3 ? 2 : 1)

const fillTrunk = (voxels, height, size, random, birch) => {
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < size; x++) {
      for (let z = 0; z < size; z++) {
        const mat = birch ? (random() < 0.2 ? 4 : 3) : 0
        voxels.set(key(x, y, z), mat)
      }
    }
  }
}

// A lumpy ellipsoid of leaves, with a few bites taken out of its edge
const fillCanopy = (voxels, random, centre, radius, squash = [1, 1.25, 1]) => {
  const span = Math.ceil(radius * Math.max(...squash.map((v) => 1 / v)))
  for (let x = -span; x <= span + 1; x++) {
    for (let y = Math.floor(centre[1] - span); y <= centre[1] + span; y++) {
      for (let z = -span; z <= span + 1; z++) {
        const d = Math.hypot(
          (x - centre[0]) * squash[0],
          (y - centre[1]) * squash[1],
          (z - centre[2]) * squash[2],
        )
        if (d > radius || (d > radius - 1 && random() < 0.35)) continue
        if (!voxels.has(key(x, y, z))) voxels.set(key(x, y, z), random() < 0.3 ? 2 : 1)
      }
    }
  }
}

export const buildTree = (random, kind = TREE_KINDS[Math.floor(random() * TREE_KINDS.length)], size = 10) => {
  const voxels = new Map()
  let trunkRadius = TREE_VOXEL * 1.2
  let canopyY
  let canopyRadius
  let offset = [-1, 0, -1]

  // Cacti: ribbed green flesh (materials 1 and 2 alternate as ribs), with
  // fruit (8) and flowers (9) on top. No bark; they're all "leaves".
  const rib = (x, z) => ((x + z) % 2 === 0 ? 1 : 2)
  if (kind === 'log') {
    // A fallen log, size voxels long, bark with mossy patches on top,
    // pale cut ends; one voxel high so it can be walked along
    for (let x = 0; x < size; x++) {
      for (let z = 0; z < 2; z++) {
        const end = x === 0 || x === size - 1
        voxels.set(key(x, 0, z), end ? 3 : random() < 0.25 ? 11 : random() < 0.3 ? 7 : 0)
      }
    }
    offset = [-size / 2, 0, -1]
    trunkRadius = 0 // nothing to bump into: it's walked on
    canopyY = TREE_VOXEL
    canopyRadius = (size / 2) * TREE_VOXEL
  } else if (kind === 'stone') {
    // A flat stepping stone, just above the water
    ;[[0, 0], [1, 0], [0, 1], [1, 1]].forEach(([x, z]) => voxels.set(key(x, 0, z), random() < 0.3 ? 11 : 10))
    offset = [-1, 0, -1]
    trunkRadius = 0
    canopyY = TREE_VOXEL
    canopyRadius = TREE_VOXEL
  } else if (kind === 'boulder') {
    // A big lumpy rock, sometimes with a smaller one leaning on it; the
    // second stone colour patches its top and sides
    const lump = (cx, cz, r, squash) => {
      const span = Math.ceil(r) + 1
      for (let x = -span; x <= span; x++) {
        for (let y = 0; y <= span; y++) {
          for (let z = -span; z <= span; z++) {
            const d = Math.hypot(x - cx, (y + 0.5) * squash, z - cz)
            if (d > r || (d > r - 1 && random() < 0.3)) continue
            voxels.set(key(x, y, z), y > r * 0.45 / squash && random() < 0.55 ? 11 : random() < 0.12 ? 11 : 10)
          }
        }
      }
    }
    const r = 2.8 + random() * 1.8
    lump(0, 0, r, 1.15 + random() * 0.3)
    if (random() < 0.5) lump(r * 0.9, r * (random() < 0.5 ? 0.6 : -0.6), r * 0.55, 1.1)
    let top = 0
    voxels.forEach((m, k) => {
      top = Math.max(top, Number(k.split(',')[1]))
    })
    offset = [0, 0, 0]
    trunkRadius = r * TREE_VOXEL * 0.9
    canopyY = (top + 1) * TREE_VOXEL
    canopyRadius = r * TREE_VOXEL
  } else if (kind === 'saguaro') {
    // A tall column with arms that bend up, fruit on the tips
    const height = 10 + Math.floor(random() * 5)
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < 2; x++) for (let z = 0; z < 2; z++) voxels.set(key(x, y, z), rib(x, z))
    }
    const arms = 1 + Math.floor(random() * 2)
    const sides = [[-1, 0], [2, 1], [0, -1], [1, 2]].sort(() => random() - 0.5)
    for (let a = 0; a < arms; a++) {
      const [ax, az] = sides[a]
      const dx = ax < 0 ? -1 : ax > 1 ? 1 : 0
      const dz = az < 0 ? -1 : az > 1 ? 1 : 0
      const y0 = 3 + Math.floor(random() * (height - 7))
      let x = ax
      let z = az
      voxels.set(key(x, y0, z), 1)
      x += dx
      z += dz
      voxels.set(key(x, y0, z), 2)
      const up = 3 + Math.floor(random() * 3)
      for (let y = y0; y <= y0 + up; y++) voxels.set(key(x, y, z), y % 2 ? 1 : 2)
      voxels.set(key(x, y0 + up + 1, z), 8)
    }
    voxels.set(key(0, height, 0), 8)
    voxels.set(key(1, height, 1), 9)
    trunkRadius = TREE_VOXEL * 1.1
    canopyY = height * TREE_VOXEL
    canopyRadius = 1.5 * TREE_VOXEL
  } else if (kind === 'barrel') {
    // Squat and round, deeply ribbed, crowned with yellow flowers and fruit
    const r = 2.4 + random() * 0.6
    const h = 4 + Math.floor(random() * 2)
    for (let x = -3; x <= 4; x++) {
      for (let z = -3; z <= 4; z++) {
        for (let y = 0; y < h; y++) {
          const squash = Math.hypot(x - 0.5, z - 0.5, (y - h * 0.45) * 0.9)
          if (squash <= r) voxels.set(key(x, y, z), rib(x, z))
        }
      }
    }
    let top = 0
    voxels.forEach((m, k) => {
      top = Math.max(top, Number(k.split(',')[1]))
    })
    ;[[0, 0], [1, 0], [0, 1], [1, 1]].forEach(([x, z]) => voxels.set(key(x, top + 1, z), random() < 0.5 ? 9 : 8))
    trunkRadius = TREE_VOXEL * 2.2
    canopyY = (top + 1) * TREE_VOXEL
    canopyRadius = 2.2 * TREE_VOXEL
  } else if (kind === 'prickly') {
    // Prickly pear: flat oval pads stacked edge on edge, studded with fruit
    const pad = (cx, cy, cz, facing) => {
      const rows = [[0, 1], [-1, 2], [-1, 2], [-1, 2], [0, 1]]
      rows.forEach(([a, b], dy) => {
        for (let i = a; i <= b; i++) {
          const x = facing ? cx + i : cx
          const z = facing ? cz : cz + i
          voxels.set(key(x, cy + dy, z), (i + dy) % 3 ? 1 : 2)
        }
      })
      // Fruit along the top edge
      const fx = facing ? cx + (random() < 0.5 ? -1 : 2) : cx
      const fz = facing ? cz : cz + (random() < 0.5 ? -1 : 2)
      voxels.set(key(fx, cy + 4, fz), 8)
      if (random() < 0.6) voxels.set(key(facing ? cx : cx, cy + 5, facing ? cz : cz), 8)
    }
    pad(0, 0, 0, true)
    pad(-1, 4, 0, false)
    pad(2, 4, 0, false)
    if (random() < 0.7) pad(-1, 8, 1, true)
    if (random() < 0.5) pad(2, 8, -1, true)
    let top = 0
    voxels.forEach((m, k) => {
      top = Math.max(top, Number(k.split(',')[1]))
    })
    trunkRadius = TREE_VOXEL * 1.2
    canopyY = (top + 1) * TREE_VOXEL
    canopyRadius = 2 * TREE_VOXEL
  } else if (kind === 'organpipe') {
    // Organ pipe: a clump of thin upright stems of different heights
    for (let x = 0; x < 3; x++) for (let z = 0; z < 3; z++) voxels.set(key(x, 0, z), rib(x, z))
    const stems = [[0, 0], [2, 0], [1, 1], [0, 2], [2, 2], [1, -1], [3, 1], [-1, 1]]
      .filter(() => random() < 0.8)
      .slice(0, 6)
    stems.forEach(([x, z]) => {
      const height = 5 + Math.floor(random() * 8)
      for (let y = 1; y < height; y++) voxels.set(key(x, y, z), y % 2 ? 1 : 2)
      if (random() < 0.5) voxels.set(key(x, height, z), random() < 0.5 ? 8 : 9)
    })
    let top = 0
    voxels.forEach((m, k) => {
      top = Math.max(top, Number(k.split(',')[1]))
    })
    trunkRadius = TREE_VOXEL * 1.6
    canopyY = (top + 1) * TREE_VOXEL
    canopyRadius = 2 * TREE_VOXEL
  } else if (kind === 'spruce') {
    // Tall, dark and narrow, tier on tier
    const trunk = 14 + Math.floor(random() * 5)
    fillTrunk(voxels, trunk, 2, random)
    const base = 3
    const tiers = trunk - base + 3
    for (let y = base; y < base + tiers; y++) {
      const r = Math.max(0, Math.round((1 - (y - base) / tiers) * 3.4 - ((y - base) % 3) * 0.6))
      for (let x = -r; x <= r + 1; x++) {
        for (let z = -r; z <= r + 1; z++) {
          if (Math.abs(x - 0.5) + Math.abs(z - 0.5) > r + 1.2) continue
          if (!voxels.has(key(x, y, z))) voxels.set(key(x, y, z), leafMat(random))
        }
      }
    }
    canopyY = (base + 2) * TREE_VOXEL
    canopyRadius = 3 * TREE_VOXEL
  } else if (kind === 'dead') {
    // A bare grey trunk with a few crooked branches
    const trunk = 8 + Math.floor(random() * 5)
    for (let y = 0; y < trunk; y++) {
      for (let x = 0; x < 2; x++) for (let z = 0; z < 2; z++) voxels.set(key(x, y, z), 7)
    }
    const branches = 3 + Math.floor(random() * 3)
    for (let b = 0; b < branches; b++) {
      const y0 = Math.floor(trunk * (0.45 + random() * 0.5))
      const dir = [[1, 0], [-1, 0], [0, 1], [0, -1]][Math.floor(random() * 4)]
      const length = 2 + Math.floor(random() * 3)
      let x = dir[0] > 0 ? 1 : 0
      let z = dir[1] > 0 ? 1 : 0
      let y = y0
      for (let i = 0; i < length; i++) {
        x += dir[0]
        z += dir[1]
        if (random() < 0.5) y++
        voxels.set(key(x, y, z), 7)
      }
    }
    canopyY = trunk * TREE_VOXEL
    canopyRadius = 2 * TREE_VOXEL
  } else if (kind === 'willow') {
    // A round crown with long curtains of leaves trailing down
    const trunk = 6 + Math.floor(random() * 3)
    fillTrunk(voxels, trunk, 2, random)
    const radius = 4 + random() * 1.2
    const cy = trunk + 2
    fillCanopy(voxels, random, [0.5, cy, 0.5], radius, [1, 1.4, 1])
    const span = Math.ceil(radius)
    for (let x = -span; x <= span + 1; x++) {
      for (let z = -span; z <= span + 1; z++) {
        const d = Math.hypot(x - 0.5, z - 0.5)
        if (d < radius - 1.6 || d > radius + 0.2 || random() < 0.35) continue
        let bottom = null
        for (let y = cy - span; y <= cy + span; y++) {
          if (voxels.has(key(x, y, z))) {
            bottom = y
            break
          }
        }
        if (bottom === null) continue
        const drop = 2 + Math.floor(random() * 5)
        for (let y = bottom - 1; y >= Math.max(1, bottom - drop); y--) voxels.set(key(x, y, z), leafMat(random))
      }
    }
    canopyY = (cy - 1) * TREE_VOXEL
    canopyRadius = (radius - 0.5) * TREE_VOXEL
  } else if (kind === 'palm') {
    // A thin leaning trunk with a crown of drooping fronds
    const trunk = 11 + Math.floor(random() * 5)
    const lean = random() < 0.5 ? 1 : -1
    let x = 0
    for (let y = 0; y < trunk; y++) {
      if (y > 0 && y % 5 === 0) x += lean
      voxels.set(key(x, y, 0), 6)
    }
    const top = trunk
    voxels.set(key(x, top, 0), 1)
    const fronds = 6 + Math.floor(random() * 3)
    for (let f = 0; f < fronds; f++) {
      const a = (f / fronds) * Math.PI * 2 + random() * 0.4
      const length = 3 + Math.floor(random() * 2)
      for (let i = 1; i <= length; i++) {
        const fx = x + Math.round(Math.cos(a) * i)
        const fz = Math.round(Math.sin(a) * i)
        const fy = top - (i > 2 ? i - 2 : 0)
        voxels.set(key(fx, fy, fz), leafMat(random))
      }
    }
    // Coconuts tucked under the crown
    voxels.set(key(x + 1, top - 1, 0), 0)
    voxels.set(key(x - 1, top - 1, 0), 0)
    trunkRadius = TREE_VOXEL * 0.7
    canopyY = (top - 1) * TREE_VOXEL
    canopyRadius = 3.5 * TREE_VOXEL
    offset = [-0.5, 0, -0.5]
  } else if (kind === 'kapok') {
    // A towering rainforest tree with a flat, spreading crown hung with vines
    const trunk = 10 + Math.floor(random() * 4)
    fillTrunk(voxels, trunk, 2, random)
    // Buttress roots
    ;[[-1, 0], [2, 1], [0, -1], [1, 2]].forEach(([bx, bz]) => {
      voxels.set(key(bx, 0, bz), 0)
      if (random() < 0.6) voxels.set(key(bx, 1, bz), 0)
    })
    const radius = 5 + random() * 1.5
    const cy = trunk + 1
    fillCanopy(voxels, random, [0.5, cy, 0.5], radius, [1, 2.2, 1])
    const span = Math.ceil(radius)
    const vines = 18 + Math.floor(random() * 10)
    for (let v = 0; v < vines; v++) {
      const vx = Math.floor((random() * 2 - 1) * span) + 1
      const vz = Math.floor((random() * 2 - 1) * span) + 1
      let bottom = null
      for (let y = cy - span; y <= cy + span; y++) {
        if (voxels.has(key(vx, y, vz))) {
          bottom = y
          break
        }
      }
      if (bottom === null) continue
      const length = 4 + Math.floor(random() * 7)
      for (let y = bottom - 1; y >= Math.max(1, bottom - length); y--) {
        if (!voxels.has(key(vx, y, vz))) voxels.set(key(vx, y, vz), 5)
        // Now and then a leaf sprouting off the vine
        if (random() < 0.15) voxels.set(key(vx + (random() < 0.5 ? 1 : -1), y, vz), 1)
      }
    }
    canopyY = (cy - 1) * TREE_VOXEL
    canopyRadius = (radius - 0.5) * TREE_VOXEL
  } else if (kind === 'pine') {
    // Stacked square tiers narrowing to a point
    const trunk = 10 + Math.floor(random() * 5)
    fillTrunk(voxels, trunk, 2, random)
    const base = 3
    const tiers = trunk - base + 2
    for (let y = base; y < base + tiers; y++) {
      const r = Math.max(0, Math.round((1 - (y - base) / tiers) * 4 - ((y - base) % 2) * 0.8))
      for (let x = -r; x <= r + 1; x++) {
        for (let z = -r; z <= r + 1; z++) {
          if (Math.abs(x - 0.5) + Math.abs(z - 0.5) > r + 1.2) continue
          if (!voxels.has(key(x, y, z))) voxels.set(key(x, y, z), random() < 0.3 ? 2 : 1)
        }
      }
    }
    canopyY = (base + 2) * TREE_VOXEL
    canopyRadius = 3.5 * TREE_VOXEL
  } else if (kind === 'birch') {
    // Thin white trunk under a small, tall crown
    const trunk = 9 + Math.floor(random() * 4)
    fillTrunk(voxels, trunk, 1, random, true)
    const radius = 2.2 + random() * 0.8
    fillCanopy(voxels, random, [0, trunk + 1, 0], radius, [1, 0.8, 1])
    trunkRadius = TREE_VOXEL * 0.7
    canopyY = (trunk - 1) * TREE_VOXEL
    canopyRadius = radius * TREE_VOXEL
    offset = [-0.5, 0, -0.5]
  } else if (kind === 'shrub') {
    // Low enough to jump onto
    const trunk = 2
    fillTrunk(voxels, trunk, 2, random)
    const radius = 2.6 + random() * 0.8
    fillCanopy(voxels, random, [0.5, trunk + 1.5, 0.5], radius, [1, 1.5, 1])
    canopyY = (trunk + 0.5) * TREE_VOXEL
    canopyRadius = (radius - 0.5) * TREE_VOXEL
  } else if (kind === 'wide') {
    // Short and spreading
    const trunk = 4 + Math.floor(random() * 3)
    fillTrunk(voxels, trunk, 2, random)
    const radius = 4 + random() * 1.5
    fillCanopy(voxels, random, [0.5, trunk + 1.5, 0.5], radius, [1, 1.8, 1])
    canopyY = trunk * TREE_VOXEL
    canopyRadius = (radius - 0.5) * TREE_VOXEL
  } else {
    const trunk = 7 + Math.floor(random() * 5)
    fillTrunk(voxels, trunk, 2, random)
    const radius = 3 + random() * 1.6
    const cy = trunk + radius * 0.6
    fillCanopy(voxels, random, [0.5, cy, 0.5], radius)
    canopyY = (cy - radius * 0.5) * TREE_VOXEL
    canopyRadius = (radius - 0.5) * TREE_VOXEL
  }

  return {
    geometry: meshVoxels(voxels, offset, TREE_VOXEL),
    trunkRadius,
    canopyY,
    canopyRadius,
    tops: treeTops(voxels),
    offset,
    voxels,
    kind,
  }
}

// Height of the top surface of each column, for standing on the canopy
const treeTops = (voxels) => {
  const tops = new Map()
  voxels.forEach((mat, k) => {
    const [x, y, z] = k.split(',').map(Number)
    const column = `${x},${z}`
    tops.set(column, Math.max(tops.get(column) || 0, (y + 1) * TREE_VOXEL))
  })
  return tops
}

// A tree after `hits` pounces: the upper leaves go first, then the rest of the
// crown, then the trunk. Returns what's left and the voxels knocked off.
export const TREE_HITS = 3
const isLeaf = (mat) => mat === 1 || mat === 2 || mat === 5 || mat === 8 || mat === 9
export const damageTree = (voxels, hits) => {
  const leafYs = []
  voxels.forEach((mat, k) => {
    if (isLeaf(mat)) leafYs.push(Number(k.split(',')[1]))
  })
  leafYs.sort((a, b) => a - b)
  const midLeaf = leafYs.length ? leafYs[Math.floor(leafYs.length / 2)] : 0
  let trunkTop = 0
  voxels.forEach((mat, k) => {
    if (!isLeaf(mat)) trunkTop = Math.max(trunkTop, Number(k.split(',')[1]) + 1)
  })
  const stump = Math.ceil(trunkTop / 2)
  const leafless = !leafYs.length
  const gone = (mat, y) =>
    hits >= TREE_HITS ||
    (leafless && hits >= 1 && y >= Math.ceil(trunkTop * 0.75)) ||
    (hits >= 2 && (isLeaf(mat) || y >= stump)) ||
    (hits >= 1 && isLeaf(mat) && y >= midLeaf)
  const kept = new Map()
  const removed = []
  voxels.forEach((mat, k) => {
    const [x, y, z] = k.split(',').map(Number)
    if (gone(mat, y)) removed.push([x, y, z, mat])
    else kept.set(k, mat)
  })
  return { voxels: kept, removed }
}
export const meshTree = (voxels, offset) => ({
  geometry: meshVoxels(voxels, offset, TREE_VOXEL),
  tops: treeTops(voxels),
})

// ----- Mushroom: materials 0 red cap, 1 white stem and spots -----
export const buildMushroom = (random) => {
  const voxels = new Map()
  for (let y = 0; y < 3; y++) voxels.set(key(0, y, 0), 1)
  for (let x = -2; x <= 2; x++) {
    for (let z = -2; z <= 2; z++) {
      voxels.set(key(x, 3, z), 0)
      if (Math.abs(x) <= 1 && Math.abs(z) <= 1) {
        voxels.set(key(x, 4, z), random() < 0.35 ? 1 : 0)
      } else if (random() < 0.2) {
        voxels.set(key(x, 3, z), 1)
      }
    }
  }
  return meshVoxels(voxels, [-0.5, 0, -0.5])
}

// ----- Spider body, facing +x: materials 0 body, 1 eyes -----
export const buildSpiderBody = () => {
  const voxels = new Map()
  // Abdomen
  for (let x = -3; x <= 0; x++) {
    for (let y = 0; y <= 2; y++) {
      for (let z = -2; z <= 1; z++) {
        const corner = (x === -3 || x === 0) && (y === 2 || z === -2 || z === 1)
        if (!corner) voxels.set(key(x, y, z), 0)
      }
    }
  }
  // Head with two red eyes
  for (let x = 1; x <= 2; x++) {
    for (let y = 0; y <= 1; y++) for (let z = -1; z <= 0; z++) voxels.set(key(x, y, z), 0)
  }
  voxels.set(key(3, 1, -1), 1)
  voxels.set(key(3, 1, 0), 1)
  return meshVoxels(voxels, [0, 1, 0])
}

// ----- Heart pickup, grown in the trees -----
const HEART_ROWS = ['.##.##.', '#######', '#######', '.#####.', '..###..', '...#...']
export const buildHeartGeometry = () =>
  meshVoxels(spriteVoxels(HEART_ROWS, () => [-1, 0], () => 0), [-3.5, -3, 0])

// ----- Biome creatures, all facing +x with their feet at y = 0 -----
const box = (voxels, [x0, x1], [y0, y1], [z0, z1], mat) => {
  for (let x = x0; x <= x1; x++) {
    for (let y = y0; y <= y1; y++) for (let z = z0; z <= z1; z++) voxels.set(key(x, y, z), mat)
  }
}

// Wolf (fog): materials 0 grey coat, 1 dark grey, 2 glowing eyes. Legs are separate.
export const WOLF_HIP = 4 // voxels up to where the legs hang from
export const buildWolfBody = () => {
  const voxels = new Map()
  box(voxels, [-6, 4], [4, 8], [-2, 2], 0) // body
  box(voxels, [-5, 3], [8, 8], [-1, 1], 1) // darker back
  box(voxels, [4, 6], [5, 9], [-2, 2], 0) // chest and neck
  box(voxels, [6, 9], [7, 10], [-2, 2], 0) // head
  box(voxels, [10, 12], [7, 8], [-1, 1], 1) // snout
  box(voxels, [7, 7], [11, 12], [-2, -2], 1) // ears
  box(voxels, [7, 7], [11, 12], [2, 2], 1)
  voxels.set(key(9, 9, -2), 2) // eyes
  voxels.set(key(9, 9, 2), 2)
  box(voxels, [-9, -7], [8, 9], [-1, 0], 1) // tail
  return meshVoxels(voxels, [0, 0, -0.5])
}
export const buildWolfLeg = () => {
  const voxels = new Map()
  box(voxels, [0, 1], [0, WOLF_HIP - 1], [0, 1], 0)
  return meshVoxels(voxels, [-1, -WOLF_HIP, -1])
}

// Polar bear (frozen peaks): materials 0 white fur, 1 cream muzzle, 2 black nose and eyes.
// Big and heavy-set; legs are separate.
export const BEAR_HIP = 5
export const buildBearBody = () => {
  const voxels = new Map()
  box(voxels, [-8, 5], [5, 12], [-4, 4], 0) // body
  box(voxels, [-6, 3], [13, 13], [-3, 3], 0) // hump
  box(voxels, [5, 8], [7, 12], [-3, 3], 0) // neck
  box(voxels, [8, 12], [8, 13], [-3, 3], 0) // head
  box(voxels, [13, 15], [8, 10], [-2, 2], 1) // muzzle
  box(voxels, [16, 16], [9, 10], [-1, 1], 2) // nose
  voxels.set(key(12, 12, -2), 2) // eyes
  voxels.set(key(12, 12, 2), 2)
  box(voxels, [9, 10], [14, 15], [-3, -3], 0) // ears
  box(voxels, [9, 10], [14, 15], [3, 3], 0)
  box(voxels, [-10, -9], [10, 11], [-1, 0], 0) // tail
  return meshVoxels(voxels, [0, 0, -0.5])
}
export const buildBearLeg = () => {
  const voxels = new Map()
  box(voxels, [0, 2], [0, BEAR_HIP - 1], [0, 2], 0)
  box(voxels, [3, 3], [0, 0], [0, 2], 1) // claws
  return meshVoxels(voxels, [-1.5, -BEAR_HIP, -1.5])
}

// Frog (rain): materials 0 green, 1 pale belly, 2 golden eyes, 3 dark spots
export const buildFrog = () => {
  const voxels = new Map()
  box(voxels, [-3, 3], [1, 3], [-3, 3], 0) // body
  box(voxels, [-3, 3], [0, 0], [-2, 2], 1) // belly
  box(voxels, [3, 5], [1, 3], [-2, 2], 0) // head
  box(voxels, [5, 5], [1, 1], [-1, 1], 1) // mouth
  box(voxels, [4, 4], [4, 4], [-2, -2], 2) // eyes on top
  box(voxels, [4, 4], [4, 4], [2, 2], 2)
  box(voxels, [-4, -2], [0, 1], [-5, -4], 0) // folded back legs
  box(voxels, [-4, -2], [0, 1], [4, 5], 0)
  box(voxels, [2, 3], [0, 0], [-4, -4], 0) // front feet
  box(voxels, [2, 3], [0, 0], [4, 4], 0)
  ;[[0, -1], [-2, 2], [1, 1], [-1, -2]].forEach(([x, z]) => voxels.set(key(x, 3, z), 3))
  return meshVoxels(voxels, [0, 0, -0.5])
}

// Snake (storm): materials 0 dark scales, 1 yellow bands, 2 red eyes, 3 tongue.
// The head is one model; the body is a chain of segments that wave behind it.
export const buildSnakeHead = () => {
  const voxels = new Map()
  box(voxels, [0, 3], [0, 2], [-1, 1], 0)
  box(voxels, [1, 2], [2, 2], [-1, 1], 1)
  voxels.set(key(3, 2, -1), 2)
  voxels.set(key(3, 2, 1), 2)
  voxels.set(key(4, 0, 0), 3)
  voxels.set(key(5, 0, 0), 3)
  return meshVoxels(voxels, [-1, 0, -0.5])
}
export const SNAKE_SEGMENT = 3 // voxels long
export const buildSnakeSegment = () => {
  const voxels = new Map()
  box(voxels, [0, SNAKE_SEGMENT - 1], [0, 1], [-1, 1], 0)
  return meshVoxels(voxels, [-SNAKE_SEGMENT / 2, 0, -0.5])
}

// ----- Nuts: each biome has its own. Materials 0 shell, 1 marks, 2 cap or husk -----
const NUT_SPRITES = {
  acorn: ACORN,
  chestnut: ['..#..', '.###.', '#####', '##o##', 'ccccc', '.ccc.'],
  walnut: ['.###.', '#o#o#', '##o##', '#o#o#', '.###.'],
  hazelnut: ['.ccc.', 'c#c#c', '.###.', '#####', '.###.', '..#..'],
  coconut: ['.###.', '#o#o#', '##o##', '#####', '#####', '.###.'],
  // A prickly pear fruit: rosy with a green crown and pale spots
  cactusfruit: ['.c.c.', '.###.', '#o###', '###o#', '#####', '.###.'],
  // A pine cone: overlapping scales, a green tuft at the top
  pinecone: ['..c..', '.#o#.', '#o#o#', '.#o#.', '#o#o#', '.#o#.', '..#..'],
}
export const NUT_KINDS = Object.keys(NUT_SPRITES)
export const buildNutGeometry = (kind) => {
  if (kind === 'acorn') return buildAcornGeometry()
  const rows = NUT_SPRITES[kind]
  const depth = (c, r) => {
    const half = (rows[r].replace(/\./g, '').length - 1) / 2
    return range(-Math.floor(half), Math.floor(half))
  }
  const mat = (c, r, ch, outer) => {
    if (ch === 'o') return outer ? 1 : 0
    if (ch === 'c') return 2
    return 0
  }
  return meshVoxels(spriteVoxels(rows, depth, mat), [-rows[0].length / 2, -rows.length / 2, -0.5])
}

// ----- Crocodile (rainforest): materials 0 green hide, 1 dark ridges, 2 eyes, 3 teeth -----
export const buildCrocBody = () => {
  const voxels = new Map()
  box(voxels, [-6, 5], [1, 3], [-2, 2], 0) // body
  for (let x = -5; x <= 4; x += 2) {
    voxels.set(key(x, 4, -1), 1)
    voxels.set(key(x, 4, 1), 1)
  }
  box(voxels, [6, 11], [1, 2], [-1, 1], 0) // long snout
  box(voxels, [6, 7], [3, 3], [-2, 2], 0) // brow
  voxels.set(key(7, 4, -2), 2) // eyes
  voxels.set(key(7, 4, 2), 2)
  ;[8, 10].forEach((x) => {
    voxels.set(key(x, 1, -2), 3) // teeth
    voxels.set(key(x, 1, 2), 3)
  })
  box(voxels, [-10, -7], [1, 2], [-1, 1], 0) // tail
  box(voxels, [-14, -11], [1, 1], [0, 0], 0)
  return meshVoxels(voxels, [0, 0, -0.5])
}

// ----- Scorpion (desert): materials 0 sandy shell, 1 dark tips, 2 eyes. Legs separate.
export const buildScorpionBody = () => {
  const voxels = new Map()
  box(voxels, [-3, 2], [1, 2], [-2, 1], 0) // body
  box(voxels, [3, 4], [1, 2], [-1, 0], 0) // head
  voxels.set(key(5, 2, -1), 2)
  voxels.set(key(5, 2, 0), 2)
  // Pincers out front
  box(voxels, [4, 7], [1, 1], [-3, -3], 0)
  box(voxels, [4, 7], [1, 1], [2, 2], 0)
  box(voxels, [7, 8], [1, 2], [-3, -2], 1)
  box(voxels, [7, 8], [1, 2], [1, 2], 1)
  return meshVoxels(voxels, [0, 0, 0])
}
// The tail, which curls up and over the back to the sting, built about its
// base (SCORPION_TAIL, in voxels) so it can swing
export const SCORPION_TAIL = [-3.5, 2]
const TAIL_VOXELS = [[-4, 2], [-5, 3], [-6, 4], [-6, 5], [-6, 6], [-5, 7], [-4, 7], [-3, 6]]
export const buildScorpionTail = () => {
  const voxels = new Map()
  TAIL_VOXELS.forEach(([x, y], i) => {
    voxels.set(key(x, y, -1), i > 5 ? 1 : 0)
    voxels.set(key(x, y, 0), i > 5 ? 1 : 0)
  })
  return meshVoxels(voxels, [-SCORPION_TAIL[0], -SCORPION_TAIL[1], 0])
}

// ----- Bat: materials 0 dark fur, 1 pale fangs and wing ribs, 2 red eyes. Wings separate.
export const buildBatBody = () => {
  const voxels = new Map()
  box(voxels, [-2, 1], [0, 3], [-1, 0], 0) // body
  box(voxels, [2, 3], [1, 3], [-1, 0], 0) // head
  voxels.set(key(3, 4, -1), 0) // ears
  voxels.set(key(3, 4, 0), 0)
  voxels.set(key(2, 5, -1), 0)
  voxels.set(key(2, 5, 0), 0)
  voxels.set(key(4, 2, -1), 2) // eyes
  voxels.set(key(4, 2, 0), 2)
  voxels.set(key(4, 1, -1), 1) // fangs
  voxels.set(key(4, 1, 0), 1)
  voxels.set(key(-3, 1, -1), 0) // feet
  voxels.set(key(-3, 1, 0), 0)
  return meshVoxels(voxels, [0, 0, 0])
}
// One wing, reaching out along +z from the shoulder: a membrane with ribs
export const buildBatWing = () => {
  const voxels = new Map()
  for (let z = 0; z < 7; z++) {
    const span = z < 2 ? [-2, 2] : z < 5 ? [-3, 1] : [-2, 0]
    for (let x = span[0]; x <= span[1]; x++) voxels.set(key(x, 0, z), x === span[1] || z === 6 ? 1 : 0)
  }
  return meshVoxels(voxels, [0, 0, 0])
}

// ----- Iguana (tropical): materials 0 green scales, 1 orange crest, 2 eyes, 3 pale belly
export const buildIguanaBody = () => {
  const voxels = new Map()
  box(voxels, [-4, 3], [1, 3], [-2, 1], 0) // body
  box(voxels, [-4, 3], [1, 1], [-1, 0], 3) // belly
  box(voxels, [4, 7], [2, 4], [-1, 0], 0) // head
  box(voxels, [4, 5], [1, 1], [-1, 0], 3) // dewlap
  voxels.set(key(6, 4, -2), 2)
  voxels.set(key(6, 4, 1), 2)
  for (let x = -3; x <= 5; x++) if (x % 2 === 0) voxels.set(key(x, x > 3 ? 5 : 4, -1), 1) // spiky crest
  box(voxels, [-9, -5], [1, 2], [-1, 0], 0) // tail
  box(voxels, [-14, -10], [1, 1], [-1, 0], 0)
  for (let x = -14; x <= -5; x += 3) voxels.set(key(x, x > -10 ? 3 : 2, -1), 1) // banded tail
  return meshVoxels(voxels, [0, 0, 0])
}

// ----- Ambient wildlife -----
// Dragonfly: a long thin body with two pairs of wings, shrunk right down
export const buildDragonfly = () => {
  const voxels = new Map()
  box(voxels, [-4, 3], [0, 0], [0, 0], 0)
  box(voxels, [-1, 0], [1, 1], [-3, -1], 0)
  box(voxels, [-1, 0], [1, 1], [1, 3], 0)
  return meshVoxels(voxels, [0, 0, -0.5], 0.7)
}

// ----- Ground plants and rocks, merged into one mesh per chunk -----
// Palette indices for DECOR_COLOURS in the component
export const DECOR = {
  stem: 0,
  red: 1,
  yellow: 2,
  white: 3,
  purple: 4,
  rock: 5,
  moss: 6,
  fern: 7,
  jungleLeaf: 8,
  pink: 9,
  orange: 10,
  branch: 11,
  pale: 12,
  darkRock: 13,
  sandRock: 14,
  bone: 15,
  tumbleweed: 16,
  shell: 17,
  starfish: 18,
}
const put = (voxels, x, y, z, mat) => {
  if (!voxels.has(key(x, y, z))) voxels.set(key(x, y, z), mat)
}
export const stampDecor = (voxels, kind, x, z, random, colour) => {
  if (kind === 'flower') {
    const tall = 1 + Math.floor(random() * 2)
    for (let y = 0; y < tall; y++) put(voxels, x, y, z, DECOR.stem)
    put(voxels, x, tall, z, colour)
    if (random() < 0.5) put(voxels, x + 1, 0, z, DECOR.stem)
  } else if (kind === 'rock') {
    const w = 1 + Math.floor(random() * 2)
    const h = 1 + Math.floor(random() * 2)
    for (let dx = 0; dx < w; dx++) {
      for (let dz = 0; dz < w; dz++) {
        for (let y = 0; y < h; y++) put(voxels, x + dx, y, z + dz, colour)
        if (random() < 0.5) put(voxels, x + dx, h, z + dz, DECOR.moss)
      }
    }
  } else if (kind === 'fern') {
    put(voxels, x, 0, z, colour)
    put(voxels, x, 1, z, colour)
    ;[[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, -1]].forEach(([dx, dz]) => {
      if (random() < 0.2) return
      put(voxels, x + dx, 1, z + dz, colour)
      put(voxels, x + dx * 2, 1, z + dz * 2, colour)
      put(voxels, x + dx * 3, 0, z + dz * 3, colour)
    })
  } else if (kind === 'bigLeaf') {
    const tall = 3 + Math.floor(random() * 2)
    for (let y = 0; y < tall; y++) put(voxels, x, y, z, DECOR.fern)
    ;[[1, 0], [-1, 0], [0, 1], [0, -1]].forEach(([dx, dz]) => {
      put(voxels, x + dx, tall, z + dz, colour)
      put(voxels, x + dx * 2, tall, z + dz * 2, colour)
      put(voxels, x + dx * 3, tall - 1, z + dz * 3, colour)
    })
    put(voxels, x, tall, z, colour)
  } else if (kind === 'paleShroom') {
    put(voxels, x, 0, z, DECOR.pale)
    for (let dx = -1; dx <= 1; dx++) for (let dz = -1; dz <= 1; dz++) put(voxels, x + dx, 1, z + dz, DECOR.pale)
  } else if (kind === 'tumbleweed') {
    // A loose ball of dry twigs
    for (let dx = 0; dx < 3; dx++) {
      for (let dy = 0; dy < 3; dy++) {
        for (let dz = 0; dz < 3; dz++) if (random() < 0.45) put(voxels, x + dx, dy, z + dz, DECOR.tumbleweed)
      }
    }
  } else if (kind === 'bones') {
    // A bleached rib cage half sunk in the sand
    const length = 4 + Math.floor(random() * 3)
    for (let i = 0; i < length; i++) put(voxels, x + i, 0, z, DECOR.bone)
    for (let i = 1; i < length; i += 2) {
      put(voxels, x + i, 1, z - 1, DECOR.bone)
      put(voxels, x + i, 1, z + 1, DECOR.bone)
    }
  } else if (kind === 'shell') {
    put(voxels, x, 0, z, colour)
    if (random() < 0.5) put(voxels, x + 1, 0, z, colour)
  } else if (kind === 'starfish') {
    put(voxels, x, 0, z, DECOR.starfish)
    ;[[1, 0], [-1, 0], [0, 1], [0, -1]].forEach(([dx, dz]) => put(voxels, x + dx, 0, z + dz, DECOR.starfish))
  } else if (kind === 'branch') {
    const along = random() < 0.5
    const length = 3 + Math.floor(random() * 3)
    for (let i = 0; i < length; i++) put(voxels, x + (along ? i : 0), 0, z + (along ? 0 : i), DECOR.branch)
  }
}
export const meshDecor = (voxels) => meshVoxels(voxels, [0, 0, 0])

// ----- Power-ups and little signs -----
// A pixel sprite extruded a voxel or two deep; palette maps each character to
// a material index
const extrude = (rows, palette, depth = [0]) =>
  meshVoxels(
    spriteVoxels(rows, () => depth, (c, r, ch) => palette[ch] || 0),
    [-rows[0].length / 2, -rows.length / 2, -0.5],
  )
// Pepper: materials 0 red, 1 green stalk
export const buildPepper = () =>
  extrude(['...g', '..gg', '.rr.', 'rrr.', 'rrr.', 'rr..', 'r...'], { r: 0, g: 1 }, [-1, 0])
// Feather: materials 0 white vane, 1 blue quill
export const buildFeather = () =>
  extrude(['....w', '...ww', '..wwb', '.wwb.', 'wwb..', 'wb...', 'b....'], { w: 0, b: 1 })
// A "z" floating over a sleeping creature
export const buildSleepZ = () => extrude(['####', '..#.', '.#..', '####'], { '#': 0 })
